import React, { useState, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { IoMdClose } from "react-icons/io";
import { toPng } from 'html-to-image';
import logo from '../../../components/images/logo.png';

import PDFPieChart from './PDFComponents/PDFPieChart.js';
import PDFTotals from './PDFComponents/PDFTotals.js';
import PDFComparePanel from './PDFComponents/PDFComparePanel.js';
import PDFParcelInfo from './PDFComponents/PDFParcelInfo.js';

import { MoonLoader } from "react-spinners";

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;
const GRID_ITEM_SIZE = 200;
const GRID_GAP = 16;

const PDFConverterModel = ({ globalState, globalRefs }) => {
  const mapContainerRef = globalRefs.mapContainerRef;
  const piechartRef = globalRefs.piechartRef;
  const PDFpiechartRef = globalRefs.PDFpiechartRef;
  const PDFTotalsRef = globalRefs.PDFTotalsRef;
  const PDFCompareRef = globalRefs.PDFCompareRef;
  const PDFParcelInfoRef = globalRefs.PDFParcelInfoRef;

  let isCreatingPDF = globalState.isCreatingPDF;
  let setIsCreatingPDF = globalState.setIsCreatingPDF;

  let isVisible = globalState.isVisible;
  let clickedInfoHeader = globalState.clickedInfoHeader;
  let longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
  let originalGeoJSONforComparingRef = globalRefs.originalGeoJSONforComparingRef;

  const geojson = globalRefs.longstandingGeoJSONRef.current;
  const isViewingPieChart = globalState.isViewingPieChart;
  const selectedMetric = globalState.selectedMetric;
  const handleLandtypePTagClick = globalState.handleLandtypePTagClick;
  const handleEnlargePieButtonClick = globalState.handleEnlargePieButtonClick;
  const selectedYearEditing = globalState.selectedYearEditing;
  const polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
  const landKeySelectedKey = globalState.landKeySelectedKey;
  const setLandKeySelectedKey = globalState.setLandKeySelectedKey;
  const selectedKey = globalState.selectedKey;
  const handleLandtypePieHover = globalState.handleLandtypePieHover;
  const longstandingGeoJSON = longstandingGeoJSONRef.current;
  const originalGeoJSONforComparing = originalGeoJSONforComparingRef.current;
  const universalParcelDifferentiator = globalState.universalParcelDifferentiator;
  const compareSelectedKey = globalState.compareSelectedKey;
  const comparingLandtypeSelectedOption = globalState.comparingLandtypeSelectedOption;
  const polygonCustomLayer = polygonCustomLayerRef.current;

  const [mapImage, setMapImage] = useState(null);
  const [pieImage, setPieImage] = useState(null);
  const [totalImage, setTotalImage] = useState(null);
  const [compareImage, setCompareImage] = useState(null);
  const [parcelImage, setParcelImage] = useState(null);

  const [isFinishedLoading, setIsFinishedLoading] = useState(false);

  useEffect(() => {
    const captureSequentialSnapshots = async () => {
      if (!isCreatingPDF) return;

      // Helper to capture one element at a time, and validate
      const captureElement = async (ref, setter, label) => {
        if (!ref || !(ref.current instanceof HTMLElement)) {
          setter(null);
          return false;
        }

        console.log(`Capturing ${label} snapshot...`);
        await new Promise(resolve => setTimeout(resolve, 300)); // small buffer for rendering

        try {
          const canvas = await html2canvas(ref.current, {
            useCORS: true,
            logging: false,
            backgroundColor: null,
            scale: 2,
            letterRendering: true,
          });

          const dataUrl = canvas.toDataURL("image/png");
          if (dataUrl && dataUrl.length > 1000) { // basic "not empty" check
            setter(dataUrl);
            return true;
          } else {
            console.warn(`${label} snapshot was empty, skipping.`);
            setter(null);
            return false;
          }
        } catch (err) {
          console.error(`Error capturing ${label} snapshot:`, err);
          setter(null);
          return false;
        }
      };

      // Sequential order
      const mapOk = await captureElement(mapContainerRef, setMapImage, "Map");
      if (!mapOk) return;

      const pieOk = await captureElement(PDFpiechartRef, setPieImage, "Pie");
      if (!pieOk) return;

      const totalOk = await captureElement(PDFTotalsRef, setTotalImage, "Totals");
      if (!totalOk) return;

      const compareOk = await captureElement(PDFCompareRef, setCompareImage, "Compare");
      if (!compareOk) return;

      console.log("✅ All snapshots captured successfully!");
    };

    captureSequentialSnapshots();
  }, [isCreatingPDF, mapContainerRef?.current, PDFpiechartRef?.current, PDFTotalsRef?.current, PDFCompareRef?.current]);

  // Capture ParcelInfo snapshot when PDF modal opens or mapRef changes

  useEffect(() => {
    const captureParcel = async () => {
      if (PDFParcelInfoRef && PDFParcelInfoRef.current instanceof HTMLElement) {
        console.log('Capturing Compare snapshot...');
        await new Promise(resolve => setTimeout(resolve, 7500));
        try {
          const canvas = await html2canvas(PDFParcelInfoRef.current, {
            useCORS: true,
            logging: false,
            backgroundColor: null,
            scale: 2,
            letterRendering: true
          });
          setParcelImage(canvas.toDataURL('image/png'));
          setIsFinishedLoading(true);
        } catch (err) {
          console.error('Error capturing Parcel snapshot:', err);
          setParcelImage(null);
        }
      } else {
        setParcelImage(null);
      }
    };
    captureParcel();
  }, [PDFCompareRef?.current, isCreatingPDF]);

  // Log the captured images for debugging
  useEffect(() => {
    console.log('Map image captured:', mapImage);
  }, [mapImage]);

  // Log the captured images for debugging
  useEffect(() => {
    console.log('Pie image captured:', pieImage);
  }, [pieImage]);

  // Log the captured images for debugging
  useEffect(() => {
    console.log('Total image captured:', totalImage);
  }, [totalImage]);

  // Log the captured images for debugging
  useEffect(() => {
    console.log('Compare image captured:', compareImage);
  }, [compareImage]);

  // Log the captured images for debugging
  useEffect(() => {
    console.log('Parcel image captured:', parcelImage);
  }, [parcelImage]);

  // Patch the PDF rendering area to include the map snapshot at the top half

  const [options, setOptions] = useState({
    pieChart: false,
    landKey: false,
    Compare: false,
    ClickedInfo: false,
  });

  const previewRef = useRef(); // For visible preview
  const pdfRef = useRef();     // For hidden full-size PDF export

  const handleToggle = (option) => {
    setOptions((prev) => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const functionOptions = [
    { key: 'pieChart', label: 'Pie Chart', color: '#ffb74d', ref: pieImage, imgWidth: '80%', imgHeight: '80%' },
    { key: 'Totals', label: 'Totals', color: '#81c784', ref: totalImage, imgWidth: '100%', imgHeight: '100%' },
    { key: 'ClickedInfo', label: 'Parcel Data', color: '#e57373', ref: parcelImage, imgWidth: '100%', imgHeight: 'auto' },
    { key: 'Compare', label: 'Compare', color: '#64b5f6', ref: compareImage },
  ];

  const toggledOptions = functionOptions.filter(opt => options[opt.key]);
  const GRID_COLS = 3;
  const GRID_ROWS = Math.ceil(toggledOptions.length / GRID_COLS);

  const renderGrid = () => {
    const topItems = toggledOptions.filter(opt =>
      opt?.key !== 'Compare'
    );
    const compareItem = toggledOptions.find(opt =>
      opt?.key === 'Compare'
    );

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, ${GRID_ITEM_SIZE}px)`,
          gridTemplateRows: `repeat(2, ${GRID_ITEM_SIZE}px)`,
          gap: GRID_GAP,
          justifyContent: 'center',
          alignContent: 'center',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'transparent'
        }}
      >

        {topItems.map((opt, idx) => {
          if (!opt || !opt.ref) return (
            <div
              key={`empty-${idx}`}
              style={{
                width: GRID_ITEM_SIZE,
                height: GRID_ITEM_SIZE,
                background: 'transparent',
              }}
            />
          );

          return (
            <div
              key={opt.key}
              style={{
                width: GRID_ITEM_SIZE,
                height: GRID_ITEM_SIZE,
                background: '#fff',
                borderRadius: 8,
                overflow: 'hidden',
                lineHeight: 0,
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                border: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img
                src={opt.ref}
                alt={`${opt.key} Snapshot`}
                style={{
                  width: opt.imgWidth || '100%',
                  height: opt.imgHeight || '100%',
                  objectFit: 'contain'
                }}
              />
            </div>
          );
        })}

        {/* Bottom Row: Compare block spanning 3 columns */}
        {compareItem && compareItem.ref && (
          <>
            <div
              style={{
                gridColumn: `span 3`,
                width: GRID_ITEM_SIZE * 3 + GRID_GAP * 2, // account for gap
                height: GRID_ITEM_SIZE,
                background: '#fff',
                borderRadius: 8,
                overflow: 'hidden',
                lineHeight: 0,
                boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                border: '1px solid #ddd'
              }}
            >
              <img
                src={compareItem.ref}
                alt="Compare Snapshot"
                style={{
                  height: '100%',
                  left: '10%',
                  top: -10,
                  position: 'relative'
                }}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const handleExportPDF = async () => {
    const element = pdfRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 4,
      useCORS: true,
      logging: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = 210;
    const pdfHeight = 297;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10); // "YYYY-MM-DD"
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // "HH-MM-SS"
    pdf.save(`Harrier_Export_${dateStr}_${timeStr}.pdf`);
  };

  // Scale for preview (e.g., 0.3 for 30% size)
  const PREVIEW_SCALE = .30;

  useEffect(() => {
    if (!pdfRef.current || !previewRef.current) return;
    // Clear existing preview
    previewRef.current.innerHTML = "";
    // Deep clone the entire pdfRef contents
    const clone = pdfRef.current.cloneNode(true);
    // Apply scale styles
    clone.style.transformOrigin = "top left";
    clone.style.pointerEvents = "none"; // Prevent interaction
    clone.style.position = "relative"; // Reset position
    clone.style.left = "0";
    clone.style.top = "0";
    clone.style.zIndex = "auto";
    // Append to preview
    previewRef.current.appendChild(clone);
  }, [pdfRef, previewRef, options]);

  const closePDFModel = () => {
    setIsCreatingPDF(false);
    setIsFinishedLoading(false);
    setMapImage(null);
    setPieImage(null);
    setTotalImage(null);
    setCompareImage(null);
    // setOptions({
    //   pieChart: false,
    //   landKey: false,
    //   Compare: false,
    //   ClickedInfo: false,
    // });
    // setLandKeySelectedKey(null);
    // if (previewRef.current) {
    //   previewRef.current.innerHTML = ""; // Clear preview
    // }
  };

  return (
    <div className="pdfconverter">

      {!isFinishedLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000000
        }}>
          <MoonLoader color="#29645D" size={50} speedMultiplier={0.5} />

          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: '#555' }}>
            Generating PDF, please wait...
          </p>
        </div>
      )}

      <div className="pdfconverterouter" style={{ color: '#222' }}>
        <p style={{ fontWeight: 'bold' }}>PDF CONVERTER</p>

        <button
          onClick={() => closePDFModel()}
          style={{
            position: 'absolute',
            top: '16px',
            width: '40px',
            height: '40px',
            right: '16px',
            textAlign: 'center',
            lineHeight: '45px',
            background: '#fff',
            zIndex: 1000,
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
            color: '#333',
            borderRadius: '50%',
            transition: 'background 0.2s, color 0.2s'
          }}
        >
          <IoMdClose />
        </button>

        <div
          className="pdfconverterinner"
          style={{
            marginTop: '75px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px'
          }}
        >

          <div className="pdfconverter-col1" style={{ width: '200px' }}>
            <ul style={{ listStyle: 'none', padding: 0, borderTop: '2px solid #E6E4ED' }}>
              {functionOptions.map((opt) => (
                <li
                  key={opt.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: '#222',
                    height: '55px',
                    borderBottom: '2px solid #E6E4ED'
                  }}
                >
                  <label style={{ flex: 1 }}>{opt.label}</label>
                  <button
                    onClick={() => handleToggle(opt.key)}
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      // border: '1px solid #ccc',
                      background: '#E6E4ED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    aria-pressed={options[opt.key]}
                  >
                    {options[opt.key] && (
                      <span
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: '#29645D'
                        }}
                      />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="pdfconverter-col2" style={{ position: 'relative' }}>
            <div
              ref={pdfRef}
              style={{
                width: A4_WIDTH,
                height: A4_HEIGHT,
                position: 'absolute',
                left: '-9999px',
                top: 0,
                background: '#fff',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: -1,
                display: 'block'
              }}
              aria-hidden="true"
            >

              <div
                style={{
                  width: '100%',
                  height: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingTop: 32,
                  boxSizing: 'border-box',
                  backgroundColor: 'white',

                }}
              >
                <div style={{
                  width: '90%',
                  backgroundColor: 'white',
                  height: '50px'
                }}>
                  <img src={logo} alt='Skylark Logo' style={{ position: 'relative', display: 'inline-block', width: '10%', float: 'left', left: '0px', backgroundColor: 'white' }} />
                  <h1 style={{
                    textAlign: 'left',
                    fontSize: 24,
                    color: '#333',
                    fontWeight: 'bold',
                    width: '90%',
                    position: 'absolute',
                    top: '-10px',
                    left: '125px'
                  }}>Harrier PDF Export</h1>
                </div>

                <p style={{
                  textAlign: 'left',
                  fontSize: 16,
                  color: '#555',
                  margin: '0 0 16px 0',
                  width: '90%'
                }}>This is a snapshot of the current map view along with selected data visualizations.</p>
                <p style={{
                  textAlign: 'left',
                  fontSize: 14,
                  color: '#777',
                  margin: '0 0 16px 0',
                  width: '90%'
                }}>Generated on: {new Date().toLocaleString()}</p>

                {mapImage && (
                  <img
                    src={mapImage}
                    alt="Map Snapshot"
                    style={{
                      width: '65%',
                      maxHeight: '100%',
                      top: 50,
                      position: 'relative',
                      objectFit: 'contain',
                      backgroundColor: '#f5f5f5',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.10)'
                    }}
                  />
                )}
              </div>

              {/* Bottom half: Grid */}
              <div
                style={{
                  width: '100%',
                  height: '50%',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end'
                }}
              >
                {renderGrid()}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 12,
                    fontSize: 12,
                    color: '#aaa',
                    pointerEvents: 'none'
                  }}
                >
                  A4 Portrait Preview
                </div>
              </div>
            </div>

            {/* Visible scaled preview */}

            <div ref={previewRef}
              style={{
                transform: `scale(${PREVIEW_SCALE})`,
                transformOrigin: 'top left',
                width: '100%',
                height: '100%',
                // position: 'relative',
                background: '#fff',
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 1,
                display: 'flex',
                marginTop: '-50px',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                padding: '16px',
              }}
              id="pdf-preview-container" />

          </div>
        </div>
        <div className="exportDivCancelArea" style={{
          backgroundColor: '#E6E4ED',
          width: '520px',
          height: '75px',
          padding: '10px',
          position: 'absolute',
          left: 0,
          textAlign: 'center',
          bottom: 0,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
        }}>
          <button
            onClick={closePDFModel}
            className="cancelPDFButton"
          >
            Cancel
          </button>
          <button
            onClick={handleExportPDF}
            className="exportPDFButton"
          >
            Export PDF
          </button>
        </div>
      </div>

      <div className="hiddenComponentArea" style={{ zIndex: '-100000', position: 'absolute', opacity: '0', pointerEvents: 'none' }}>
        <PDFPieChart
          geojson={geojson}
          isViewingPieChart={isViewingPieChart}
          selectedMetric={selectedMetric}
          handleLandtypePTagClick={handleLandtypePTagClick}
          handleEnlargePieButtonClick={handleEnlargePieButtonClick}
          selectedYearEditing={selectedYearEditing}
          polygonCustomLayerRef={polygonCustomLayerRef}
          landKeySelectedKey={landKeySelectedKey}
          setLandKeySelectedKey={setLandKeySelectedKey}
          selectedKey={selectedKey}
          handleLandtypePieHover={handleLandtypePieHover}
          PDFpiechartRef={PDFpiechartRef}
        />
        <PDFTotals
          isVisible={isVisible}
          polygonCustomLayerRef={polygonCustomLayerRef}
          clickedInfoHeader={clickedInfoHeader}
          globalState={globalState}
          longstandingGeoJSONRef={longstandingGeoJSONRef}
          originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
          globalRefs={globalRefs}
        />
        <PDFComparePanel
          longstandingGeoJSONRef={longstandingGeoJSONRef}
          selectedMetric={selectedMetric}
          selectedYearEditing={selectedYearEditing}
          universalParcelDifferentiator={universalParcelDifferentiator}
          landKeySelectedKey={landKeySelectedKey}
          comparingLandtypeSelectedOption={comparingLandtypeSelectedOption}
          originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
          isVisible={isVisible}
          PDFCompareRef={PDFCompareRef}
          isCreatingPDF={isCreatingPDF}
        />
        <PDFParcelInfo
          isVisible={isVisible}
          polygonCustomLayerRef={polygonCustomLayerRef}
          clickedInfoHeader={clickedInfoHeader}
          globalState={globalState}
          longstandingGeoJSONRef={longstandingGeoJSONRef}
          originalGeoJSONforComparingRef={originalGeoJSONforComparingRef}
          globalRefs={globalRefs}
          PDFParcelInfoRef={PDFParcelInfoRef}
        />

      </div>

    </div>
  );
};

export default PDFConverterModel;


// import React, { useState, useRef, useEffect } from 'react';
// import html2canvas from 'html2canvas';
// import jsPDF from 'jspdf';
// import { IoMdClose } from "react-icons/io";
// import { toPng } from 'html-to-image';
// import logo from '../../../components/images/logo.png';

// import PDFPieChart from './PDFComponents/PDFPieChart.js';
// import PDFTotals from './PDFComponents/PDFTotals.js';
// import PDFComparePanel from './PDFComponents/PDFComparePanel.js';
// import PDFParcelInfo from './PDFComponents/PDFParcelInfo.js';

// import { MoonLoader } from "react-spinners";
// import { map } from '../../core/globalVariables.js';

// const A4_WIDTH = 794;
// const A4_HEIGHT = 1123;
// const GRID_ITEM_SIZE = 200;
// const GRID_GAP = 16;

// const PDFConverterModel = ({ globalState, globalRefs }) => {
//   const mapContainerRef = globalRefs.mapContainerRef;

//   // PDF REFS

//   const PDFpiechartRef = globalRefs.PDFpiechartRef;
//   const PDFTotalsRef = globalRefs.PDFTotalsRef;
//   const PDFCompareRef = globalRefs.PDFCompareRef;
//   const PDFParcelInfoRef = globalRefs.PDFParcelInfoRef;

//   // REAL REFS

//   const piechartRef = globalRefs.piechartRef;
//   const TotalsRef = globalRefs.TotalsRef;
//   const CompareRef = globalRefs.CompareRef;

//   // 

//   let isCreatingPDF = globalState.isCreatingPDF;
//   let setIsCreatingPDF = globalState.setIsCreatingPDF;

//   let isVisible = globalState.isVisible;
//   const isComparing = globalState.isComparing;
//   const setisComparing = globalState.setisComparing;
//   let clickedInfoHeader = globalState.clickedInfoHeader;
//   let longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
//   let originalGeoJSONforComparingRef = globalRefs.originalGeoJSONforComparingRef;

//   const geojson = globalRefs.longstandingGeoJSONRef.current;
//   const isViewingPieChart = globalState.isViewingPieChart;
//   const selectedMetric = globalState.selectedMetric;
//   const handleLandtypePTagClick = globalState.handleLandtypePTagClick;
//   const handleEnlargePieButtonClick = globalState.handleEnlargePieButtonClick;
//   const selectedYearEditing = globalState.selectedYearEditing;
//   const polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
//   const landKeySelectedKey = globalState.landKeySelectedKey;
//   const setLandKeySelectedKey = globalState.setLandKeySelectedKey;
//   const selectedKey = globalState.selectedKey;
//   const handleLandtypePieHover = globalState.handleLandtypePieHover;
//   const longstandingGeoJSON = longstandingGeoJSONRef.current;
//   const originalGeoJSONforComparing = originalGeoJSONforComparingRef.current;
//   const universalParcelDifferentiator = globalState.universalParcelDifferentiator;
//   const compareSelectedKey = globalState.compareSelectedKey;
//   const comparingLandtypeSelectedOption = globalState.comparingLandtypeSelectedOption;
//   const polygonCustomLayer = polygonCustomLayerRef.current;

//   // 

//   const isLeftDrawerOpen = globalState.isLeftDrawerOpen;
//   const setisLeftDrawerOpen = globalState.setisLeftDrawerOpen;
//   const isLeftSubDrawerOpen = globalState.isLeftSubDrawerOpen;
//   const setisLeftSubDrawerOpen = globalState.setisLeftSubDrawerOpen;
//   const setisViewingPieChart = globalState.setisViewingPieChart;
//   const setIsVisible = globalState.setIsVisible;
//   const setActiveTab = globalState.setActiveTab;
//   const setIsRighdivrawerVisible = globalState.setIsRighdivrawerVisible;

//   //

//   const [mapImage, setMapImage] = useState(null);
//   const [pieImage, setPieImage] = useState(null);
//   const [totalImage, setTotalImage] = useState(null);
//   const [compareImage, setCompareImage] = useState(null);
//   const [parcelImage, setParcelImage] = useState(null);

//   const [isFinishedLoading, setIsFinishedLoading] = useState(false);

//   useEffect(() => {
//     if (!isCreatingPDF) return;

//     const delay = (ms) => new Promise((res) => setTimeout(res, ms));

//     const captureElement = async (ref, setter, label, retries = 10) => {
//       for (let i = 0; i < retries; i++) {
//         if (ref?.current instanceof HTMLElement) {
//           console.log(`Capturing ${label} snapshot...`);
//           await delay(150);
//           try {
//             const canvas = await html2canvas(ref.current, {
//               useCORS: true,
//               logging: false,
//               backgroundColor: null,
//               scale: 2,
//               letterRendering: true
//             });
//             setter(canvas.toDataURL("image/png"));
//             return true; // success
//           } catch (err) {
//             console.error(`Error capturing ${label}:`, err);
//             setter(null);
//             return false;
//           }
//         }
//         await delay(150); // wait for it to render
//       }
//       console.warn(`${label} element not found after retries`);
//       setter(null);
//       return false;
//     };

//     const runFlow = async () => {
//       // STEP 1: MAP
//       await captureElement(mapContainerRef, setMapImage, "Map");

//       // STEP 2: OPEN PIE
//       setisLeftDrawerOpen(true);
//       setisLeftSubDrawerOpen(true);
//       setisViewingPieChart(true);

//       // Wait until pie is actually in the DOM
//       await captureElement(piechartRef, setPieImage, "Pie");

//       await delay(500); // ensure pie is fully rendered

//       setisViewingPieChart(false);
//       setisLeftSubDrawerOpen(false);

//       await delay(500); // allow pie drawer to close

//       // STEP 3: OPEN TOTALS
//       setIsVisible(true);
//       setActiveTab("TOTAL");
//       setIsRighdivrawerVisible(true);

//       await captureElement(TotalsRef, setTotalImage, "Totals");

//       setisComparing(true);

//       await delay(1000); // wait for compare panel to render
//       await captureElement(CompareRef, setCompareImage, "Compare");

//       await delay(500); // ensure parcel info is rendered

//       setActiveTab("GROUP")

//       await captureElement(TotalsRef, setParcelImage, "Parcel Info");

//       // All done

//       await delay(500);

//       setisLeftDrawerOpen(false);
//       setisComparing(false);
//       setIsVisible(false);
//       setIsRighdivrawerVisible(false);

//       // Hide the pdfconverterouter div during PDF generation
//       const pdfconverterouter = document.querySelector('.pdfconverterouter');
//       if (pdfconverterouter) {
//         pdfconverterouter.style.opacity = '0';
//       }

//       await delay(1000);

//       handleExportPDF();

//       setIsCreatingPDF(false);

//     };

//     runFlow();
//   }, [
//     isCreatingPDF,
//     setisLeftDrawerOpen,
//     setisLeftSubDrawerOpen,
//     setisViewingPieChart,
//     setIsVisible,
//     setActiveTab,
//     setIsRighdivrawerVisible
//   ]);

//   useEffect(() => {
//     if (mapImage && pieImage && totalImage && compareImage && parcelImage && isCreatingPDF) {
//       setTimeout(() => {
//         setIsFinishedLoading(true);
//       }, 500);
//     } else {
//       console.error("One or more images failed to capture.");
//       setIsFinishedLoading(false);
//     }
//   }, [mapImage, pieImage, totalImage, compareImage, parcelImage, isCreatingPDF]);

//   // Log the captured images for debugging
//   useEffect(() => {
//     console.log('Map image captured:', mapImage);
//   }, [mapImage]);

//   // Log the captured images for debugging
//   useEffect(() => {
//     console.log('Pie image captured:', pieImage);
//   }, [pieImage]);

//   // Log the captured images for debugging
//   useEffect(() => {
//     console.log('Total image captured:', totalImage);
//   }, [totalImage]);

//   // Log the captured images for debugging
//   useEffect(() => {
//     console.log('Compare image captured:', compareImage);
//   }, [compareImage]);

//   // Log the captured images for debugging
//   useEffect(() => {
//     console.log('Parcel image captured:', parcelImage);
//   }, [parcelImage]);

//   // Patch the PDF rendering area to include the map snapshot at the top half

//   const [options, setOptions] = useState({
//     pieChart: true,
//     landKey: true,
//     Totals: true,
//     Compare: true,
//     ClickedInfo: false,
//   });

//   useEffect(() => {
//     if (isVisible) {
//       setOptions({
//         pieChart: true,
//         landKey: true,
//         Totals: true,
//         Compare: true,
//         ClickedInfo: true,
//       });
//     }
//   }, [isVisible]);

//   const previewRef = useRef(); // For visible preview
//   const pdfRef = useRef();     // For hidden full-size PDF export

//   const handleToggle = (option) => {
//     setOptions((prev) => ({
//       ...prev,
//       [option]: !prev[option]
//     }));
//   };

//   const functionOptions = [
//     { key: 'pieChart', label: 'Pie Chart', color: '#ffb74d', ref: pieImage }, // pieImage is the image data URL
//     { key: 'Totals', label: 'Totals', color: '#81c784', ref: totalImage },
//     { key: 'ClickedInfo', label: 'Parcel Data', color: '#e57373', ref: parcelImage },
//     { key: 'Compare', label: 'Compare', color: '#64b5f6', ref: compareImage }, // mapImage is the image data URL
//   ];

//   const [imageStyles, setImageStyles] = useState({
//     mapImage: { scale: 1, x: 0, y: 5, width: '100%', height: 'auto', maxHeight: '300px' },
//     pieChart: { scale: 1, x: 0, y: 30, width: '100%', height: '230%' },
//     Totals: { scale: 1, x: 0, y: -40, width: '100%', height: '450%' },
//     Compare: { scale: 1, x: 0, y: -80, width: '150%', height: 'auto' },
//     ClickedInfo: { scale: 1, x: 0, y: 0, width: '100%', height: 'auto' },
//   });

//   const toggledOptions = functionOptions.filter(opt => options[opt.key]);
//   const GRID_COLS = 3;
//   const GRID_ROWS = Math.ceil(toggledOptions.length / GRID_COLS);

//   const renderGrid = () => {
//     const topItems = toggledOptions.filter(opt =>
//       opt?.key !== 'Compare'
//     );
//     const compareItem = toggledOptions.find(opt =>
//       opt?.key === 'Compare'
//     );

//     return (
//       <div
//         style={{
//           width: '100%',
//           height: '100%',
//           display: 'grid',
//           gridTemplateColumns: `repeat(${GRID_COLS}, ${GRID_ITEM_SIZE}px)`,
//           gridTemplateRows: `repeat(2, ${GRID_ITEM_SIZE}px)`,
//           gap: GRID_GAP,
//           justifyContent: 'center',
//           alignContent: 'center',
//           position: 'absolute',
//           bottom: 0,
//           left: 0,
//           right: 0,
//           background: 'transparent'
//         }}
//       >

//         {topItems.map((opt, idx) => {
//           if (!opt || !opt.ref) return (
//             <div
//               key={`empty-${idx}`}
//               style={{
//                 width: GRID_ITEM_SIZE,
//                 height: GRID_ITEM_SIZE,
//                 background: 'transparent'
//               }}
//             />
//           );

//           return (
//             <div
//               key={opt.key}
//               style={{
//                 width: GRID_ITEM_SIZE,
//                 height: GRID_ITEM_SIZE,
//                 background: '#fff',
//                 borderRadius: 0,
//                 overflow: 'hidden',
//                 lineHeight: 0,
//                 boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
//                 border: '1px solid #ddd'
//               }}
//             >
//               <img
//                 src={opt.ref}
//                 alt={`${opt.key} Snapshot`}
//                 style={{
//                   transform: `translate(${imageStyles[opt.key]?.x || 0}px, ${imageStyles[opt.key]?.y || 0}px) scale(${imageStyles[opt.key]?.scale || 1})`,
//                   width: imageStyles[opt.key]?.width || '100%',
//                   height: imageStyles[opt.key]?.height || '100%',
//                   objectFit: 'contain',
//                   position: 'relative'
//                 }}
//               />
//             </div>
//           );
//         })}

//         {/* Bottom Row: Compare block spanning 3 columns */}
//         {compareItem && compareItem.ref && (
//           <div
//             style={{
//               gridColumn: `span 3`,
//               width: GRID_ITEM_SIZE * 3 + GRID_GAP * 2, // account for gap
//               height: GRID_ITEM_SIZE,
//               background: '#fff',
//               borderRadius: 0,
//               overflow: 'hidden',
//               lineHeight: 0,
//               boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
//               border: '1px solid #ddd'
//             }}
//           >
//             <img
//               src={compareItem.ref}
//               alt="Compare Snapshot"
//               style={{
//                 transform: `translate(${imageStyles.Compare?.x || 0}px, ${imageStyles.Compare?.y || 0}px) scale(${imageStyles.Compare?.scale || 1})`,
//                 width: imageStyles.Compare?.width || '100%',
//                 height: imageStyles.Compare?.height || '100%',
//                 objectFit: 'contain',
//                 position: 'relative'
//               }}
//             />
//           </div>
//         )}
//       </div>
//     );
//   };

//   const handleExportPDF = async () => {
//     const element = pdfRef.current;
//     if (!element) return;

//     const canvas = await html2canvas(element, {
//       scale: 4,
//       useCORS: true,
//       logging: false
//     });

//     const imgData = canvas.toDataURL('image/png');
//     const pdf = new jsPDF({
//       orientation: 'portrait',
//       unit: 'mm',
//       format: 'a4'
//     });

//     const pdfWidth = 210;
//     const pdfHeight = 297;
//     pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
//     const now = new Date();
//     const dateStr = now.toISOString().slice(0, 10); // "YYYY-MM-DD"
//     const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // "HH-MM-SS"
//     pdf.save(`Harrier_Export_${dateStr}_${timeStr}.pdf`);
//   };

//   // Scale for preview (e.g., 0.3 for 30% size)
//   const PREVIEW_SCALE = .25;

//   useEffect(() => {
//     if (!pdfRef.current || !previewRef.current) return;
//     previewRef.current.innerHTML = "";
//     const clone = pdfRef.current.cloneNode(true);
//     clone.style.transformOrigin = "top left";
//     clone.style.pointerEvents = "none"; // Prevent interaction
//     clone.style.position = "relative"; // Reset position
//     clone.style.left = "0";
//     clone.style.top = "0";
//     clone.style.zIndex = "auto";
//     if (mapImage && pieImage && totalImage && compareImage && parcelImage) {
//       previewRef.current.appendChild(clone);
//     }
//   }, [pdfRef, previewRef, options, mapImage, pieImage, totalImage, compareImage, parcelImage]);

//   const closePDFModel = () => {
//     setIsCreatingPDF(false);
//     setIsFinishedLoading(false);
//     if (previewRef.current) {
//       previewRef.current.innerHTML = ""; // Clear preview
//     }
//   };

//   return (
//     <div className="pdfconverter">

//       {!isFinishedLoading && (
//         <div style={{
//           position: 'absolute',
//           top: 0,
//           left: 0,
//           width: '100%',
//           height: '100%',
//           backgroundColor: 'rgba(255, 255, 255, 1)',
//           display: 'flex',
//           flexDirection: 'column',
//           alignItems: 'center',
//           justifyContent: 'center',
//           zIndex: 100000000
//         }}>
//           <MoonLoader
//             color="#29645D"
//             size={50}
//             speedMultiplier={0.5}
//           />
//           <div
//             className="loading_text"
//             style={{
//               marginTop: "20px",
//               textAlign: "center",
//               transition: "opacity 0.5s ease-in-out",
//               opacity: 1,
//               fontSize: "12px",
//             }}
//           >
//             <p style={{ margin: "0px", fontSize: "14px", marginTop: "65px" }}>
//               {isFinishedLoading ? "All set! Downloading your PDF..." : "Generating PDF, please wait..."}
//             </p>
//           </div>
//         </div>
//       )}

//       <div className="pdfconverterouter">
//         <p style={{ fontWeight: 'bold' }}>PDF CONVERTER</p>

//         <button
//           onClick={() => closePDFModel()}
//           style={{
//             position: 'absolute',
//             top: '16px',
//             width: '40px',
//             height: '40px',
//             right: '16px',
//             textAlign: 'center',
//             lineHeight: '45px',
//             background: '#fff',
//             zIndex: 1000,
//             border: 'none',
//             cursor: 'pointer',
//             fontSize: '24px',
//             color: '#333',
//             borderRadius: '50%',
//             transition: 'background 0.2s, color 0.2s'
//           }}
//         >
//           <IoMdClose />
//         </button>

//         <div
//           className="pdfconverterinner"
//           style={{
//             marginTop: '75px',
//             display: 'grid',
//             gridTemplateColumns: '1fr 1fr',
//             gap: '24px'
//           }}
//         >

//           <div className="pdfconverter-col2" style={{ position: 'relative', left: '100px' }}>
//             <div
//               ref={pdfRef}
//               style={{
//                 width: A4_WIDTH,
//                 height: A4_HEIGHT,
//                 position: 'absolute',
//                 left: '-9999px',
//                 top: 0,
//                 background: '#fff',
//                 overflow: 'hidden',
//                 pointerEvents: 'none',
//                 zIndex: -1,
//                 display: 'block'
//               }}
//               aria-hidden="true"
//             >

//               <div
//                 style={{
//                   width: '100%',
//                   height: '50%',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   alignItems: 'center',
//                   justifyContent: 'flex-start',
//                   paddingTop: 32,
//                   boxSizing: 'border-box',
//                   backgroundColor: 'white',

//                 }}
//               >
//                 <div style={{
//                   width: '90%',
//                   backgroundColor: 'white',
//                   height: '50px'
//                 }}>
//                   <img src={logo} alt='Harrier Logo' style={{ position: 'relative', display: 'inline-block', width: '10%', float: 'left', left: '0px', backgroundColor: 'white' }} />
//                   <h1 style={{
//                     textAlign: 'left',
//                     fontSize: 24,
//                     color: '#333',
//                     fontWeight: 'bold',
//                     width: '90%',
//                     position: 'absolute',
//                     top: '-10px',
//                     left: '125px'
//                   }}>Harrier PDF Export</h1>
//                 </div>

//                 <p style={{
//                   textAlign: 'left',
//                   fontSize: 16,
//                   color: '#555',
//                   margin: '0 0 16px 0',
//                   width: '90%'
//                 }}>This is a snapshot of the current map view along with selected data visualizations.</p>
//                 <p style={{
//                   textAlign: 'left',
//                   fontSize: 14,
//                   color: '#777',
//                   margin: '0 0 16px 0',
//                   width: '90%'
//                 }}>Generated on: {new Date().toLocaleString()}</p>

//                 {mapImage && (
//                   <img
//                     src={mapImage}
//                     alt="Map Snapshot"
//                     style={{
//                       transform: `translate(${imageStyles.mapImage.x}px, ${imageStyles.mapImage.y}px) scale(${imageStyles.mapImage.scale})`,
//                       width: imageStyles.mapImage.width,
//                       height: imageStyles.mapImage.height,
//                       objectFit: 'contain',
//                       backgroundColor: '#f5f5f5',
//                       boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
//                       position: 'relative',
//                     }}
//                   />
//                 )}
//               </div>

//               {/* Bottom half: Grid */}
//               <div
//                 style={{
//                   width: '100%',
//                   height: '50%',
//                   position: 'relative',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   alignItems: 'center',
//                   justifyContent: 'flex-end'
//                 }}
//               >
//                 {renderGrid()}
//                 <div
//                   style={{
//                     position: 'absolute',
//                     bottom: 8,
//                     right: 12,
//                     fontSize: 12,
//                     color: '#aaa',
//                     pointerEvents: 'none'
//                   }}
//                 >
//                   {/* A4 Portrait Preview */}
//                 </div>
//               </div>
//             </div>

//             {/* Visible scaled preview */}

//             <div ref={previewRef}
//               style={{
//                 transform: `scale(${PREVIEW_SCALE})`,
//                 transformOrigin: 'top left',
//                 width: '100%',
//                 height: '100%',
//                 position: 'relative',
//                 background: '#fff',
//                 overflow: 'hidden',
//                 pointerEvents: 'none',
//                 zIndex: 1,
//                 display: 'flex',
//                 flexDirection: 'column',
//                 alignItems: 'center',
//                 justifyContent: 'flex-end',
//                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
//                 borderRadius: '0px',
//                 padding: '16px',
//               }}
//               id="pdf-preview-container" />

//           </div>
//         </div>
//         {/* <button
//           onClick={handleExportPDF}
//           className="exportPDFButton"
//         >
//           Export PDF
//         </button> */}
//       </div>

//     </div>
//   );
// };

// export default PDFConverterModel;
